import React from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';

const ArticleAnalysisCard = ({ article }) => {
  const {
    id,
    title,
    url,
    publication_date,
    overall_rating,
    truthfulness_score,
    constitutional_accuracy_score,
    source_credibility_score,
    rhetoric_score,
    summary,
    publishers = [],
    authors = []
  } = article;
  
  // Format date if available
  const formattedDate = publication_date 
    ? new Date(publication_date).toLocaleDateString()
    : 'Unknown date';
  
  // Get publisher data if available
  const publisher = publishers.length > 0 ? publishers[0] : null;
  const publisherName = publisher ? publisher.name : 'Unknown publisher';
  const publisherUrl = publisher && publisher.url ? publisher.url : null;
  
  // Get author data if available
  const author = authors.length > 0 ? authors[0] : null;
  const authorName = author ? author.name : 'Unknown author';
  const authorUrl = author && author.url ? author.url : null;
  
  // Generate a URL-friendly slug from the title
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .substring(0, 50)
      .replace(/-+$/, '');      // Remove trailing dashes
  };
  
  // Get rating label based on score
  const getRatingLabel = (score) => {
    if (score === null || score === undefined) return 'Not Rated';
    if (score <= 30) return 'Misleading';
    if (score <= 60) return 'Somewhat Misleading';
    if (score <= 80) return 'Mostly Accurate';
    return 'Accurate';
  };
  
  // Get color class based on score - more subtle, site-consistent colors
  const getScoreColorClass = (score) => {
    if (score === null || score === undefined || score === 0) return 'bg-gray-200 text-gray-700';
    if (score <= 30) return 'bg-slate-200 text-slate-800';
    if (score <= 60) return 'bg-slate-300 text-slate-800';
    return 'bg-slate-400 text-slate-800';
  };
  
  // Get progress bar color based on score - more subtle, site-consistent colors
  const getProgressBarColorClass = (score) => {
    if (score === null || score === undefined || score === 0) return 'bg-gray-400';
    if (score <= 30) return 'bg-slate-400';
    if (score <= 60) return 'bg-slate-500';
    return 'bg-blue-600';
  };
  
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden border max-w-4xl mx-auto md:grid md:grid-cols-12 md:gap-0">
      {/* Mobile Layout - Article info first, then scores */}
      <div className="block md:hidden">
        {/* Article Info - Moved above scores for mobile only */}
        <div className="p-4">
          <h3 className="font-bold text-lg mb-2 line-clamp-2">{title}</h3>
          
          <div className="flex flex-wrap text-xs text-gray-600 mb-3">
            <div className="mr-3">
              <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer nofollow"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                View Original Article
              </a>
            </div>
            <div className="mr-3">
              <span className="font-medium">Publisher:</span>{' '}
              {publisherUrl ? (
                <a 
                  href={publisherUrl} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow" 
                  className="text-blue-600 hover:text-blue-800"
                >
                  {publisherName}
                </a>
              ) : (
                publisherName
              )}
            </div>
            <div className="mr-3">
              <span className="font-medium">Author:</span>{' '}
              {authorUrl ? (
                <a 
                  href={authorUrl} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow" 
                  className="text-blue-600 hover:text-blue-800"
                >
                  {authorName}
                </a>
              ) : (
                authorName
              )}
            </div>
            <div>
              <span className="font-medium">Date:</span> {formattedDate}
            </div>
          </div>
        </div>
        
        {/* Score section for mobile - removed large score circle */}
        <div className="p-4 bg-gray-100">
          {/* No header or large score badge for cleaner mobile view */}
          
          {/* Explanation of Score Meaning */}
          {overall_rating !== null && (
            <div className="mb-3 bg-gray-50 p-2 rounded border border-gray-100">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-800">
                  <span className="font-medium">Analysis:</span> Rated as{' '}
                  <span className={`font-bold ${
                    overall_rating <= 30 ? 'text-slate-700' : 
                    overall_rating <= 60 ? 'text-slate-700' : 
                    'text-blue-700'
                  }`}>
                    {getRatingLabel(overall_rating)}
                  </span>
                  {overall_rating <= 60 && (
                    <span> with accuracy concerns</span>
                  )}
                </p>
                <div className={`text-sm font-bold px-2 py-1 rounded-full ${getScoreColorClass(overall_rating)}`}>
                  {overall_rating}/100
                </div>
              </div>
            </div>
          )}
          
          {/* Individual score bars */}
          <div className="w-full space-y-2 mb-4">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Truthfulness</span>
                <span className="font-medium">{truthfulness_score || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(truthfulness_score)}`} 
                     style={{ width: `${truthfulness_score || 0}%` }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Constitutional</span>
                <span className="font-medium">{constitutional_accuracy_score || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(constitutional_accuracy_score)}`} 
                     style={{ width: `${constitutional_accuracy_score || 0}%` }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Credibility</span>
                <span className="font-medium">{source_credibility_score || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(source_credibility_score)}`} 
                     style={{ width: `${source_credibility_score || 0}%` }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Rhetoric</span>
                <span className="font-medium">{rhetoric_score || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(rhetoric_score)}`} 
                     style={{ width: `${rhetoric_score || 0}%` }}></div>
              </div>
            </div>
          </div>
          
          {/* Summary if available - more lines shown on mobile */}
          {summary && (
            <div className="mb-3">
              <h4 className="font-semibold text-xs text-gray-700 mb-1">Summary:</h4>
              <p className="text-sm text-gray-800 line-clamp-4">{summary}</p>
            </div>
          )}
          
          {/* View full analysis link */}
          <div className="flex justify-end mt-2">
            <Link 
              to={`/a/${id}/${generateSlug(title)}.html`}
              className="inline-block bg-slate-100 hover:bg-slate-200 text-blue-600 text-sm font-medium py-1.5 px-3 rounded border border-slate-300"
            >
              View Full Analysis
            </Link>
          </div>
        </div>
      </div>
      
      {/* Desktop Layout - Original layout with scores on left, article info on right */}
      <div className="hidden md:flex md:col-span-3 bg-gray-100 flex-col justify-center items-center p-4">
        <div className="flex flex-col items-center w-full">
          <div className="text-center mb-2">
            <div className="text-lg font-bold text-gray-800">Overall Score</div>
            <div className="flex items-center justify-center text-sm text-gray-500">
              <span>Higher = More Accurate</span>
              <div className="relative group ml-1">
                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                <div className="absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 w-48 text-xs text-gray-700 bottom-full left-1/2 transform -translate-x-1/2 mb-1">
                  Our accuracy scores range from 0-100. Higher scores indicate more accurate and credible content.
                </div>
              </div>
            </div>
          </div>
          
          {/* Overall score badge - using more subtle, site-consistent colors */}
          <div className={`text-center font-bold text-3xl rounded-full w-20 h-20 flex items-center justify-center mb-4 ${
            overall_rating <= 30 ? 'bg-slate-100 text-slate-700 border-2 border-slate-300' : 
            overall_rating <= 60 ? 'bg-slate-200 text-slate-700 border-2 border-slate-300' : 
            'bg-blue-50 text-blue-700 border-2 border-blue-200'
          }`}>
            {overall_rating || '?'}
          </div>
          
          {/* Individual score bars */}
          <div className="w-full space-y-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Truthfulness</span>
                <span className="font-medium">{truthfulness_score || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(truthfulness_score)}`} 
                     style={{ width: `${truthfulness_score || 0}%` }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Constitutional</span>
                <span className="font-medium">{constitutional_accuracy_score || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(constitutional_accuracy_score)}`} 
                     style={{ width: `${constitutional_accuracy_score || 0}%` }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Credibility</span>
                <span className="font-medium">{source_credibility_score || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(source_credibility_score)}`} 
                     style={{ width: `${source_credibility_score || 0}%` }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Rhetoric</span>
                <span className="font-medium">{rhetoric_score || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(rhetoric_score)}`} 
                     style={{ width: `${rhetoric_score || 0}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Desktop-only Article Info portion */}
      <div className="hidden md:block md:col-span-9 p-4">
        <h3 className="font-bold text-lg mb-2 line-clamp-2">{title}</h3>
        
        <div className="flex flex-wrap text-xs text-gray-600 mb-3">
          <div className="mr-3">
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer nofollow"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              View Original Article
            </a>
          </div>
          <div className="mr-3">
            <span className="font-medium">Publisher:</span>{' '}
            {publisherUrl ? (
              <a 
                href={publisherUrl} 
                target="_blank" 
                rel="noopener noreferrer nofollow" 
                className="text-blue-600 hover:text-blue-800"
              >
                {publisherName}
              </a>
            ) : (
              publisherName
            )}
          </div>
          <div className="mr-3">
            <span className="font-medium">Author:</span>{' '}
            {authorUrl ? (
              <a 
                href={authorUrl} 
                target="_blank" 
                rel="noopener noreferrer nofollow" 
                className="text-blue-600 hover:text-blue-800"
              >
                {authorName}
              </a>
            ) : (
              authorName
            )}
          </div>
          <div>
            <span className="font-medium">Date:</span> {formattedDate}
          </div>
        </div>
        
        {/* Explanation of Score Meaning */}
        {overall_rating !== null && (
          <div className="mb-3 bg-gray-50 p-2 rounded border border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-800">
                <span className="font-medium">Analysis:</span> Rated as{' '}
                <span className={`font-bold ${
                  overall_rating <= 30 ? 'text-slate-700' : 
                  overall_rating <= 60 ? 'text-slate-700' : 
                  'text-blue-700'
                }`}>
                  {getRatingLabel(overall_rating)}
                </span>
                {overall_rating <= 60 && (
                  <span> with accuracy concerns</span>
                )}
              </p>
              <div className={`text-sm font-bold px-2 py-1 rounded-full ${getScoreColorClass(overall_rating)}`}>
                {overall_rating}/100
              </div>
            </div>
          </div>
        )}
        
        {/* Show scores in 4-column grid - with more subtle, site-consistent styling */}
        {overall_rating !== null && (
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="p-2 bg-slate-50 rounded border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase font-semibold text-slate-700">Truthfulness</div>
                <div className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${getScoreColorClass(truthfulness_score)}`}>
                  {truthfulness_score > 0 ? truthfulness_score : 'N/A'}
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(truthfulness_score)}`} 
                     style={{ width: `${truthfulness_score || 0}%` }}></div>
              </div>
            </div>
            
            <div className="p-2 bg-slate-50 rounded border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase font-semibold text-slate-700">Constitutional</div>
                <div className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${getScoreColorClass(constitutional_accuracy_score)}`}>
                  {constitutional_accuracy_score > 0 ? constitutional_accuracy_score : 'N/A'}
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(constitutional_accuracy_score)}`} 
                     style={{ width: `${constitutional_accuracy_score || 0}%` }}></div>
              </div>
            </div>
            
            <div className="p-2 bg-slate-50 rounded border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase font-semibold text-slate-700">Credibility</div>
                <div className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${getScoreColorClass(source_credibility_score)}`}>
                  {source_credibility_score > 0 ? source_credibility_score : 'N/A'}
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(source_credibility_score)}`} 
                     style={{ width: `${source_credibility_score || 0}%` }}></div>
              </div>
            </div>
            
            <div className="p-2 bg-slate-50 rounded border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase font-semibold text-slate-700">Rhetoric</div>
                <div className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${getScoreColorClass(rhetoric_score)}`}>
                  {rhetoric_score > 0 ? rhetoric_score : 'N/A'}
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${getProgressBarColorClass(rhetoric_score)}`} 
                     style={{ width: `${rhetoric_score || 0}%` }}></div>
              </div>
            </div>
          </div>
        )}
        
        {/* Summary if available - desktop version shows 5 lines */}
        {summary && (
          <div className="mb-3">
            <h4 className="font-semibold text-xs text-gray-700 mb-1">Summary:</h4>
            <p className="text-sm md:text-base text-gray-800 md:line-clamp-5 line-clamp-2">{summary}</p>
          </div>
        )}
        
        {/* View full analysis link - less prominent styling */}
        <div className="flex justify-end mt-2">
          <Link 
            to={`/a/${id}/${generateSlug(title)}.html`}
            className="inline-block bg-slate-100 hover:bg-slate-200 text-blue-600 text-sm font-medium py-1.5 px-3 rounded border border-slate-300"
          >
            View Full Analysis
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ArticleAnalysisCard;